import React, { useRef } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import Headroom from 'react-headroom';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/router';

import Filter from '../../molecules/filter/filter';
import NavigationButton from '../navigation/navigationButton';
import LogoIcon from '../../icons/logo/logo';
import SearchButton from '../search-bar/searchButton';
import LanguageSwitch from '../../molecules/language-switch/languageSwitch';
import AccessibilityButton from '../../molecules/accessibility-meta/accessibilityButton';
import StyledHeader from './header.style';
import HeaderModal from '../header-modal/headerModal';

const Header = props => {
  const {
    navIsOpen,
    searchIsOpen,
    accessibilityIsOpen,
    hasFilter = false,
    noLogo = false,
    backgroundGradient = false,
    inverted = false,
    invertedLogo = false,
    stageType = 'default',
    disableScroll = false,
    hasBanner = false
  } = props;

  const locale = useLocale();
  const router = useRouter();
  const header = useRef();
  const currentlyInverted = inverted || accessibilityIsOpen;

  return (
    <StyledHeader
      ref={header}
      backgroundGradient={backgroundGradient}
      inverted={currentlyInverted}
      invertedLogo={invertedLogo}
      accessibilityIsOpen={accessibilityIsOpen}
      {...props}
      noLogo={noLogo}
    >
      <Headroom disable={accessibilityIsOpen || disableScroll} pinStart={hasBanner ? 200 : 0}>
        <div className="header__wrapper">
          {!accessibilityIsOpen && (
            <div className="header__logo-wrapper header-filter-box">
              <Link
                href={{
                  pathname: '/time',
                  query: { slug: 'index', lang: locale, filters: router.query.filters || null }
                }}
                as={`/${locale}`}
                className="header__logo"
                aria-label="Homepage"
              >
                <LogoIcon />
                <span className="sr-only">FUTURIUM Homepage</span>
              </Link>
            </div>
          )}

          {hasFilter && stageType !== 'campaign' && (
            <Filter insideHeader className="header__filter" />
          )}

          <div className="header__button-wrapper">
            <div className="header-filter-box">
              <NavigationButton inverted={currentlyInverted} close={navIsOpen} />
              <SearchButton
                className="header-filter-box-search"
                inverted={currentlyInverted}
                close={searchIsOpen}
              />
              <AccessibilityButton
                className="header-filter-box-accessibility"
                inverted={currentlyInverted}
                close={accessibilityIsOpen}
              />

              <LanguageSwitch inverted={currentlyInverted} className="header__language-switch" />
            </div>
          </div>
        </div>
        <HeaderModal />
      </Headroom>
    </StyledHeader>
  );
};

Header.propTypes = {
  locale: PropTypes.oneOf([PropTypes.shape(), PropTypes.arrayOf(PropTypes.shape())]),
  hasFilter: PropTypes.bool,
  noLogo: PropTypes.bool,
  backgroundGradient: PropTypes.bool,
  disableScroll: PropTypes.bool,
  inverted: PropTypes.bool,
  invertedLogo: PropTypes.bool,
  navIsOpen: PropTypes.bool.isRequired,
  searchIsOpen: PropTypes.bool.isRequired,
  accessibilityIsOpen: PropTypes.bool.isRequired,
  hasBanner: PropTypes.bool,
  stageType: PropTypes.string
};

export { Header as HeaderComponent };
export default Header;
